import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Mail, Loader2, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error } = await resetPassword(email);

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      setSuccess(true);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen gradient-mesh flex items-center justify-center p-5">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-emerald/20 flex items-center justify-center mx-auto mb-6 animate-scale-in">
            <CheckCircle className="w-10 h-10 text-emerald" />
          </div>
          <h1 className="font-display text-2xl font-bold text-text-primary mb-2">Check Your Email</h1>
          <p className="text-text-muted mb-6">
            We've sent a password reset link to <span className="text-gold">{email}</span>
          </p>
          <Link to="/login" className="text-gold hover:text-gold-light font-medium inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-mesh flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8 animate-slide-up">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center mx-auto mb-4 shadow-gold">
            <span className="font-display text-3xl font-bold text-base">CC</span>
          </div>
          <h1 className="font-display text-3xl font-bold text-text-primary">Reset Password</h1>
          <p className="text-text-muted mt-2">We'll send you a reset link</p>
        </div>

        {/* Reset Form */}
        <div className="glass-card gold-border rounded-2xl p-6 animate-slide-up stagger-2">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-ruby/10 border border-ruby/20 rounded-xl mb-4">
              <AlertCircle className="w-5 h-5 text-ruby flex-shrink-0" />
              <p className="text-sm text-ruby">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="reset-email" className="block text-xs text-text-muted uppercase tracking-wider mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" aria-hidden="true" />
                <input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-elevated border border-subtle rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:ring-1 focus:ring-gold/20 transition-all"
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-gold py-3 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>
        </div>

        {/* Back to Login */}
        <p className="text-center mt-6 animate-slide-up stagger-3">
          <Link to="/login" className="text-gold hover:text-gold-light font-medium inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </p>
      </div>
    </div>
  );
}
